import { AuthServerMessage, AuthClientMessage } from "../types";

/**
 * Handles authentication messaging between the parent window and an embedded iframe.
 * Sends user credentials to the iframe and listens for an authentication response.
 */
export class AuthManager {
  private _iframe: HTMLIFrameElement;
  private _iframeUrl: URL;
  private _apiKey: AuthClientMessage["apiKey"];
  private _userId: AuthClientMessage["userId"];
  private _skipAuth: boolean;
  public tier: string;

  /**
   * Creates an instance of AuthManager.
   *
   * @param {HTMLIFrameElement} iframe - The target iframe element to manage.
   * @param {string} [userId="anon"] - The user ID to authenticate with.
   * @param {string} [apiKey="anon"] - The API key to authenticate with.
   */
  constructor(
    iframe: typeof this._iframe,
    userId?: typeof this._userId,
    apiKey?: typeof this._apiKey,
    skipAuth?: boolean,
  ) {
    this._apiKey = apiKey ?? "anon";
    this._userId = userId ?? "anon";
    this.tier = "free";
    this._iframe = iframe;
    this._iframeUrl = new URL(iframe.src);
    this._skipAuth = skipAuth ?? false;
  }

  /**
   * Initializes the AuthManager by:
   * - Sending an authentication message to the iframe with the API key and user ID.
   * - Listening for a response message of type "auth" to retrieve and store the access tier.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when the authentication response is received.
   */
  async init(
    opt: { try: number; maxTries: number } = { try: 1, maxTries: 6 },
  ): Promise<"timeout" | "initialized"> {
    console.log(
      "[sandai-core] [AuthManager] Initializing Sandai Auth Manager...",
    );
    if (this._skipAuth) {
      console.warn(
        "[sandai-core] [AuthManager] Auth was skipped. Skipping auth is against " +
          "TOS for live apps. For debugging, this is fine and should be turned on, " +
          "since authorized access is used for counting towards your session limit.\n" +
          "(very exploitable, but you risk losing access to sandai if abused)",
      );
      return "initialized";
    }
    if (opt.try > opt.maxTries) {
      console.error(
        "[sandai-core] [AuthManager] Failed to authorize after",
        opt.maxTries,
        " tries. Setting to Free tier",
      );
      this.tier = "Free";
      return "timeout";
    }
    const authPromise = new Promise<"timeout" | "initialized">((resolve) => {
      this._destroyListeners();
      this._listenForMessage((data: AuthServerMessage) => {
        if (data.type === "auth") {
          this.tier = data.tier;
          console.log("Initialized Sandai Auth Manager!");
          console.log("Auth message received: ", data);
          resolve("initialized");
        }
      });

      this._sendMessage({
        type: "auth",
        apiKey: this._apiKey,
        userId: this._userId,
      } as AuthClientMessage);
    });
    const timeoutPromise = new Promise<"timeout" | "initialized">((resolve) => {
      setTimeout(() => {
        resolve("timeout");
      }, 1000 * opt.try);
    });

    const racePromise = Promise.race([authPromise, timeoutPromise]);
    const initResult = await racePromise;
    if (initResult === "timeout") {
      return await this.init({ ...opt, try: opt.try + 1 });
    }
    return initResult;
  }

  /**
   * Sends a message to the iframe.
   *
   * @private
   * @param {*} data - The data payload to be sent to the iframe.
   * @throws {Error} Throws an error if the iframe does not have a `contentWindow`.
   */
  private _sendMessage(data: AuthClientMessage) {
    if (data.type !== "auth") {
      throw new Error(
        `[sandai-client] [AuthManager] Auth manager can only send 'auth' requests. Got type '${data.type}' in data ${JSON.stringify(data)}`,
      );
    }
    if (this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage(data, "*");
    } else {
      throw new Error("iframe doesn't have a 'contentWindow' member");
    }
  }

  /**
   * Stores all registered message event listeners for later removal.
   *
   * @private
   */
  private _listeners: Array<(event: MessageEvent<any>) => void> = [];

  /**
   * Listens for messages from the iframe and invokes the provided callback when a message is received.
   * Only messages from the iframe's origin are processed.
   *
   * The listener is stored internally so it can be removed later via `destroy()`.
   *
   * @private
   * @param {(data: any) => void} callback - The callback function to invoke with the message data.
   */
  private _listenForMessage(callback: (data: any) => void) {
    console.log("[sandai-core] [AuthManager] Creating Auth Listener...");
    const listener = (event: MessageEvent<any>) => {
      if (event.origin !== this._iframeUrl.origin) return;
      callback(event.data);
    };
    this._listeners.push(listener);
    window.addEventListener("message", listener);
    console.log("[sandai-core] [AuthManager] Listening for Auth Messages...");
  }

  private _destroyListeners() {
    console.log("[sandai-core] [AuthManager] Destroying Auth Listeners...");
    this._listeners.forEach((listener) => {
      window.removeEventListener("message", listener);
    });
    this._listeners = [];
    console.log("[sandai-core] [AuthManager] Destroyed Auth Listeners");
  }

  /**
   * Cleans up all registered message event listeners previously added by `_listenForMessage`.
   * Call this method when the instance is being destroyed or no longer needs to listen to messages.
   */
  destroy() {
    console.log("[sandai-core] [AuthManager] Destroying Auth Manager...");
    this._destroyListeners();
    console.log("[sandai-core] [AuthManager] Destroyed Auth Manager");
  }
}
